import { useEffect, useState, useMemo } from 'react';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import WorkerLayout from '../../components/WorkerLayout';
import { useTheme } from '../../components/AdminLayout';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  return d;
}

function formatDate(d) {
  return d.toISOString().slice(0, 10);
}

function WorkerScheduleContent() {
  const { T, S } = useTheme();
  const accent = T.orange;

  const [weekOffset, setWeekOffset] = useState(0);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);

  const weekStart = useMemo(() => {
    const today = new Date();
    const start = getWeekStart(today);
    start.setDate(start.getDate() + weekOffset * 7);
    return start;
  }, [weekOffset]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const startDate = formatDate(weekDays[0]);
  const endDate = formatDate(weekDays[6]);

  useEffect(() => {
    setLoading(true);
    api.getWorkerSchedule(startDate, endDate)
      .then(setSchedule)
      .catch(() => setSchedule([]))
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const todayStr = formatDate(new Date());

  // Index bookings by date
  const bookingsByDate = useMemo(() => {
    const map = {};
    schedule.forEach(item => {
      map[item.date] = item.bookings || [];
    });
    return map;
  }, [schedule]);

  const statusColors = {
    confirmed: { bg: '#fef3c7', color: '#92400e' },
    in_progress: { bg: '#dbeafe', color: '#1d4ed8' },
    completed: { bg: '#dcfce7', color: '#15803d' },
  };

  return (
    <WorkerLayout>
      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={S.pageTitle}>Schedule</h1>
        <div style={{ width: '40px', height: '3px', background: accent, borderRadius: '2px', marginTop: '0.3rem' }} />
      </div>

      {/* Week nav */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.25rem',
      }}>
        <button
          onClick={() => setWeekOffset(w => w - 1)}
          style={{
            padding: '0.4rem 0.9rem', borderRadius: '8px',
            border: `1px solid ${T.border}`, background: '#fff',
            fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600,
            color: T.text, cursor: 'pointer',
          }}
        >
          ← Prev
        </button>
        <div style={{
          fontFamily: "'Sora', sans-serif", fontSize: '0.95rem', fontWeight: 600,
          color: T.white,
        }}>
          {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          {' — '}
          {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {weekOffset !== 0 && (
            <button
              onClick={() => setWeekOffset(0)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '8px',
                border: 'none', background: accent,
                fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600,
                color: '#fff', cursor: 'pointer',
              }}
            >
              Today
            </button>
          )}
          <button
            onClick={() => setWeekOffset(w => w + 1)}
            style={{
              padding: '0.4rem 0.9rem', borderRadius: '8px',
              border: `1px solid ${T.border}`, background: '#fff',
              fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 600,
              color: T.text, cursor: 'pointer',
            }}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Week grid */}
      {loading ? (
        <p style={{ color: T.muted }}>Loading schedule...</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '0.5rem',
        }}>
          {weekDays.map((day, i) => {
            const dateStr = formatDate(day);
            const isToday = dateStr === todayStr;
            const dayBookings = bookingsByDate[dateStr] || [];

            return (
              <div key={dateStr} style={{
                ...S.card,
                padding: '0',
                borderColor: isToday ? accent : T.border,
                borderWidth: isToday ? '2px' : '1px',
                overflow: 'hidden',
                minHeight: '200px',
              }}>
                {/* Day header */}
                <div style={{
                  padding: '0.6rem 0.75rem',
                  background: isToday ? `${accent}12` : T.bgCard2,
                  borderBottom: `1px solid ${isToday ? `${accent}30` : T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', fontWeight: 700,
                    color: isToday ? accent : T.muted,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>
                    {DAY_NAMES[i]}
                  </span>
                  <span style={{
                    fontFamily: "'Sora', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                    color: isToday ? accent : T.text,
                  }}>
                    {day.getDate()}
                  </span>
                </div>

                {/* Bookings */}
                <div style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  {dayBookings.length === 0 ? (
                    <div style={{
                      fontSize: '0.72rem', color: T.muted, textAlign: 'center',
                      padding: '1.5rem 0.5rem', fontFamily: "'DM Sans', sans-serif",
                    }}>
                      No bookings
                    </div>
                  ) : (
                    dayBookings.map(b => {
                      const sc = statusColors[b.status] || statusColors.confirmed;
                      return (
                        <div key={b.id} style={{
                          padding: '0.4rem 0.55rem',
                          borderRadius: '6px',
                          background: sc.bg,
                          borderLeft: `3px solid ${sc.color}`,
                        }}>
                          <div style={{
                            fontFamily: "'Sora', sans-serif", fontSize: '0.7rem', fontWeight: 700,
                            color: sc.color,
                          }}>
                            {b.booking_time?.slice(0, 5) || '--:--'}
                          </div>
                          <div style={{
                            fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem',
                            color: T.text, marginTop: '0.15rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {b.service_name || 'Service'}
                          </div>
                          <div style={{
                            fontFamily: "'DM Sans', sans-serif", fontSize: '0.62rem',
                            color: T.muted, marginTop: '0.1rem',
                          }}>
                            {b.client_name || 'Walk-in'}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </WorkerLayout>
  );
}

export default function WorkerSchedule() {
  return <ProtectedRoute requireWorker><WorkerScheduleContent /></ProtectedRoute>;
}
